// src/components/WalletConnect.tsx

import { useScore } from '@/context/ScoreContext';
import { useTonConnectUI, useTonWallet, useTonAddress, TonConnectButton } from '@tonconnect/ui-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/auth.css';

import { completeGoogleRedirectSignIn, signInWithGoogle } from '@/config/users';
import { useUser } from '@/context/UserContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';


const WalletConnect = () => {
    const navigate = useNavigate();
    const wallet = useTonWallet();
    const walletAddress = useTonAddress();
    const [tonConnectUI] = useTonConnectUI();
    const { toggleGameState } = useScore();
    const { setUser } = useUser();



    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    const hydrateUserAndEnterGame = async (uid: string) => {
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
            throw new Error("User profile not found after authentication.");
        }

        const data: any = userSnap.data();
        setUser({
            uid,
            username: data.username,
            walletId: data.walletId ?? null,
            points: data.points ?? 0,
            bananium: data.bananium ?? 0,
            token404: data.token404 ?? 0,
            lastBananiumClaimTimestamp: data?.lastBananiumClaimTimestamp,
            virtualWalletId: data?.virtualWalletId ?? null,
            telegramId: data?.telegramId ?? null,
            isAdmin: data?.isAdmin ?? false,
        });

        navigate('/game', { replace: true });
    };

    useEffect(() => {
        let mounted = true;
        const checkRedirectGoogleAuth = async () => {
            const result = await completeGoogleRedirectSignIn();
            if (!mounted) return;
            if (result.success) {
                try {
                    await hydrateUserAndEnterGame((result as any).uid);
                } catch (error: any) {
                    setAuthError(error?.message || "Failed to continue after Google login.");
                }
            }
        };

        checkRedirectGoogleAuth();
        return () => {
            mounted = false;
        };
    }, [navigate]);

    useEffect(() => {
        let isActive = true;

        const handleAuthFlow = async () => {
            if (!wallet) {
                setAuthError("Wallet not connected.");
                return;
            }

            setIsAuthenticating(true);
            setAuthError(null);

            try {
                // Simplified: Skip signing, directly navigate to login
                console.log("[WalletConnect] Wallet connected. Redirecting to login...");

                if (isActive) {
                    navigate('/login', {
                        state: {
                            walletAddress: walletAddress,
                        },
                        replace: true
                    });
                }

            } catch (error: any) {
                console.error("[WalletConnect] Flow failed:", error);
                if (isActive) {
                    setAuthError(error.message || "Failed to proceed to login.");
                }
            } finally {
                if (isActive) {
                    setIsAuthenticating(false);
                }
            }
        };

        if (wallet) {
            handleAuthFlow();
        } else {
            setIsAuthenticating(false);
            setAuthError(null);
        }

        toggleGameState(false);

        return () => {
            isActive = false;
        };

    }, [wallet, tonConnectUI, navigate, toggleGameState]);


    const handleGoogleLogin = async () => {
        setIsAuthenticating(true);
        setAuthError(null);
        const result = await signInWithGoogle();
        if (result.success) {
            // If redirect flow starts, browser will navigate automatically.
            if ((result as any).redirect) {
                setIsAuthenticating(false);
                return;
            }
            try {
                await hydrateUserAndEnterGame((result as any).uid);
            } catch (error: any) {
                setAuthError(error?.message || "Failed to continue after Google login.");
            }
        } else {
            setAuthError(result.error || "Google login failed");
        }
        setIsAuthenticating(false);
    };

    // const handleTelegramLogin = async () => {
    //     // For now, modal or simple placeholder
    //     setAuthError("Telegram login is being configured. Please use Google or Wallet for now.");
    // };

    const authStatusMessage = isAuthenticating ? "Authenticating..." : authError ? `Authentication Failed: ${authError}` : null;


    return (
        <div className="wallet-connect-container">
            <div className="wallet-connect-content">
                <div className="wallet-connect-header">
                    <h1 className="game-title">ColourView</h1>
                    <div className="title-underline"></div>
                </div>

                <div className="wallet-connect-body">
                    <h2>Enter ColourView</h2>
                    <p className="wallet-connect-description">
                        Choose your login method to start your adventure
                    </p>

                    <div className="login-methods">
                        {/* <TonConnectButton className="wallet-connect-button" /> */}

                        {/* <div className="social-login-separator">
                            <span>or</span>
                        </div> */}

                        <button className="social-login-button google" onClick={handleGoogleLogin} disabled={isAuthenticating}>
                            <img src="/google.svg" alt="Google" style={{ width: 20, height: 20 }} />
                            Continue with Google
                        </button>

                        <div className="social-login-button telegram" >
                            <span>🔹</span>
                            <TonConnectButton className="wallet-connect-button" />
                        </div>
                    </div>

                    {(isAuthenticating) && (
                        <div className="connecting-indicator">
                            <div className="connecting-spinner"></div>
                            <p>{authStatusMessage}</p>
                        </div>
                    )}

                    {authError && (
                        <div className="auth-error-message">
                            {authStatusMessage}
                        </div>
                    )}

                </div>

                <div className="wallet-connect-footer">
                    <p className="wallet-security-note">
                        🔒 Secure connection powered by TON
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WalletConnect;
